import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Eye, XCircle, Sparkles, ExternalLink, Sliders } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v, { sign = false } = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = Number(v);
  const p = sign && n > 0 ? '+' : '';
  return `${p}$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const BUCKETS = {
  critical: { icon: XCircle, cls: 'text-red-500', ring: 'ring-red-500/30', bg: 'bg-red-500/5' },
  watch: { icon: AlertTriangle, cls: 'text-yellow-500', ring: 'ring-yellow-500/30', bg: 'bg-yellow-500/5' },
  positive: { icon: CheckCircle2, cls: 'text-emerald-500', ring: 'ring-emerald-500/30', bg: 'bg-emerald-500/5' },
};

/**
 * Reusable Portfolio Action Center.
 *
 * Modes:
 *   variant="full"     — full 3-column layout (used on My Portfolio)
 *   variant="compact"  — condensed for Dashboard hero
 *
 * All items are backend-driven; no fabricated data. Deep-links open the
 * relevant property page or the Scenario Analyzer prefilled with that deal.
 */
export default function ActionCenter({ variant = 'full', premiumOnly = true }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/portfolio-doctor/action-center`, {
          withCredentials: true,
        });
        setData(resp.data);
      } catch (e) {
        if (e.response?.status === 403) setGated(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 h-40" />
    );
  }

  if (gated) {
    if (!premiumOnly) return null;
    return null; // parent shows its own paywall
  }

  if (!data) return null;

  const formatValues = (v) => {
    const out = { ...v };
    if (out.amount !== undefined) out.amount = fmtMoney(out.amount, { sign: true });
    return out;
  };

  const renderItem = (item, bucket, i) => {
    const Cfg = BUCKETS[bucket];
    const Icon = Cfg.icon;
    return (
      <div
        key={`${bucket}-${i}`}
        data-testid={`ac-item-${bucket}-${i}`}
        className={`flex items-start gap-3 p-3 rounded-xl ${Cfg.bg} ring-1 ${Cfg.ring}`}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${Cfg.cls}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug">
            {t(`actionCenter.items.${item.key}`, formatValues(item.values || {}))}
          </p>
          {(item.deal_id || item.scenario_deal_id) && (
            <div className="flex flex-wrap gap-3 mt-1.5">
              {item.deal_id && (
                <button
                  data-testid={`ac-open-${bucket}-${i}`}
                  onClick={() => navigate(`/deal/${item.deal_id}`)}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#12355B] dark:text-[#45C04A] hover:underline"
                >
                  {t('actionCenter.openProperty')} <ExternalLink className="w-2.5 h-2.5" />
                </button>
              )}
              {item.scenario_deal_id && (
                <button
                  data-testid={`ac-scenario-${bucket}-${i}`}
                  onClick={() => navigate(`/scenario?deal_id=${item.scenario_deal_id}`)}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400 hover:underline"
                >
                  {t('actionCenter.runScenario')} <Sliders className="w-2.5 h-2.5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const isEmpty =
    data.critical.length === 0 && data.watch.length === 0 && data.positive.length === 0;

  if (variant === 'compact') {
    return (
      <div
        data-testid="action-center-compact"
        className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-card"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#45C04A]" />
            <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white">
              {t('actionCenter.title')}
            </h3>
          </div>
          <button
            onClick={() => navigate('/portfolio-doctor')}
            className="text-[10px] uppercase tracking-wider font-bold text-[#12355B] dark:text-[#45C04A] hover:underline"
          >
            {t('actionCenter.viewMore')}
          </button>
        </div>
        {isEmpty ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 italic">{t('actionCenter.empty')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-red-500">
                🔴 {t('actionCenter.critical')} · {data.critical.length}
              </p>
              {data.critical.slice(0, 2).map((it, i) => renderItem(it, 'critical', i))}
            </div>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-yellow-500">
                🟡 {t('actionCenter.watch')} · {data.watch.length}
              </p>
              {data.watch.slice(0, 2).map((it, i) => renderItem(it, 'watch', i))}
            </div>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-500">
                🟢 {t('actionCenter.positive')} · {data.positive.length}
              </p>
              {data.positive.slice(0, 2).map((it, i) => renderItem(it, 'positive', i))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // FULL variant
  return (
    <div
      data-testid="action-center-full"
      className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#45C04A]/15">
          <Sparkles className="w-5 h-5 text-[#45C04A]" />
        </div>
        <div>
          <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">
            {t('actionCenter.title')}
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">{t('actionCenter.subtitle')}</p>
        </div>
      </div>

      {isEmpty ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic">
          {t('actionCenter.empty')}
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <BucketColumn
            title={`🔴 ${t('actionCenter.critical')}`}
            count={data.critical.length}
            items={data.critical}
            bucket="critical"
            renderItem={renderItem}
          />
          <BucketColumn
            title={`🟡 ${t('actionCenter.watch')}`}
            count={data.watch.length}
            items={data.watch}
            bucket="watch"
            renderItem={renderItem}
          />
          <BucketColumn
            title={`🟢 ${t('actionCenter.positive')}`}
            count={data.positive.length}
            items={data.positive}
            bucket="positive"
            renderItem={renderItem}
          />
        </div>
      )}
    </div>
  );
}

function BucketColumn({ title, count, items, bucket, renderItem }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
          {title}
        </p>
        <span className="text-[10px] font-mono text-slate-400">{count}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic px-3 py-2">—</div>
      ) : (
        items.map((it, i) => renderItem(it, bucket, i))
      )}
    </div>
  );
}
